import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { hexToHslString, lightenHsl } from "./colorUtils";

/**
 * Color theme registry. Each entry overrides --primary/--ring/--chart-1 via
 * the matching `[data-theme="<id>"]` block in index.css. The "coral" default
 * has no data-theme override (it lives in :root/.dark directly).
 */
export const COLOR_THEMES = [
  { id: "coral",   name: "Coral",     description: "Calidez foodtech (defecto)", swatch: "#E0653D", swatchDark: "#F58157" },
  { id: "violet",  name: "Violeta",   description: "SaaS premium · Linear/Vercel", swatch: "#7C3AED", swatchDark: "#A78BFA" },
  { id: "emerald", name: "Esmeralda", description: "Sostenible · orgánico", swatch: "#059669", swatchDark: "#34D399" },
  { id: "ocean",   name: "Océano",    description: "Fintech · profesional", swatch: "#0EA5E9", swatchDark: "#38BDF8" },
  { id: "magenta", name: "Magenta",   description: "Creativo · casual", swatch: "#EC4899", swatchDark: "#F472B6" },
  { id: "copper",  name: "Cobre",     description: "Gastronomía premium", swatch: "#D97706", swatchDark: "#F59E0B" },
];

const USER_KEY = "menugenius-color-theme";
const RESTAURANT_KEY = "menugenius-color-theme-by-restaurant";
const ACCENT_KEY = "menugenius-accent-color-by-restaurant";
const DEFAULT_THEME = "coral";

const isValid = (id) => id === "custom" || COLOR_THEMES.some((t) => t.id === id);
const isHex = (s) => typeof s === "string" && /^#[0-9a-f]{6}$/i.test(s.trim());

const readUser = () => {
  try {
    const v = window.localStorage.getItem(USER_KEY);
    return v && isValid(v) ? v : DEFAULT_THEME;
  } catch { return DEFAULT_THEME; }
};

const readMap = () => {
  try {
    const raw = window.localStorage.getItem(RESTAURANT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
};

const writeMap = (map) => {
  try { window.localStorage.setItem(RESTAURANT_KEY, JSON.stringify(map)); } catch { /* ignore */ }
};

const readAccentMap = () => {
  try {
    const raw = window.localStorage.getItem(ACCENT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
};

const writeAccentMap = (map) => {
  try { window.localStorage.setItem(ACCENT_KEY, JSON.stringify(map)); } catch { /* ignore */ }
};

const CUSTOM_PROPS = ["--primary", "--ring", "--chart-1"];

const applyCustomAccent = (hex) => {
  const root = document.documentElement;
  const baseHsl = hexToHslString(hex);
  if (!baseHsl) return false;
  const isDark = root.classList.contains("dark");
  const finalHsl = isDark ? lightenHsl(baseHsl, 12) : baseHsl;
  root.setAttribute("data-theme", "custom");
  CUSTOM_PROPS.forEach((p) => root.style.setProperty(p, finalHsl));
  return true;
};

const clearCustomAccent = () => {
  const root = document.documentElement;
  CUSTOM_PROPS.forEach((p) => root.style.removeProperty(p));
};

const applyTheme = (id, accent = null) => {
  const root = document.documentElement;
  // Always strip any prior inline accent styles before re-applying
  clearCustomAccent();
  if (id === "custom" && accent) {
    applyCustomAccent(accent);
    return;
  }
  if (!id || id === DEFAULT_THEME) {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", id);
  }
};

/**
 * Hook for reading and switching the active color theme.
 *
 * Themes have two scopes:
 *   1. **User default**  — applied when the current restaurant has no override.
 *   2. **Per-restaurant** — overrides the user default when the user is
 *      working on that specific restaurant. Useful for managers who want
 *      a distinct visual identity per location.
 *
 * The "active" theme is `restaurantOverride ?? userTheme`. The topbar toggle
 * smartly updates whichever scope is currently active, so toggling on a
 * restaurant with an override updates that restaurant only, while toggling
 * on a restaurant without an override updates the user default.
 */
export function useColorTheme(restaurantId = null) {
  const [userTheme, setUserThemeState] = useState(readUser);
  const [overrides, setOverrides] = useState(readMap);
  const [accents, setAccents] = useState(readAccentMap);

  const restaurantOverride = restaurantId ? overrides[restaurantId] : null;
  const validOverride = isValid(restaurantOverride) ? restaurantOverride : null;
  const restaurantAccent = restaurantId ? accents[restaurantId] : null;
  const validAccent = isHex(restaurantAccent) ? restaurantAccent : null;
  const activeTheme = validOverride || userTheme;
  const activeAccent = activeTheme === "custom" ? validAccent : null;

  // Apply on mount and whenever the active theme/accent changes
  useEffect(() => {
    applyTheme(activeTheme, activeAccent);
  }, [activeTheme, activeAccent]);

  // Re-apply when dark mode is toggled (custom hex needs lightness adjust)
  useEffect(() => {
    if (activeTheme !== "custom") return;
    const observer = new MutationObserver(() => {
      applyTheme(activeTheme, activeAccent);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [activeTheme, activeAccent]);

  // Cross-tab sync
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === USER_KEY) {
        const next = e.newValue && isValid(e.newValue) ? e.newValue : DEFAULT_THEME;
        setUserThemeState(next);
      } else if (e.key === RESTAURANT_KEY) {
        setOverrides(readMap());
      } else if (e.key === ACCENT_KEY) {
        setAccents(readAccentMap());
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  /** Set the user's default theme (applies to any restaurant without override). */
  const setUserTheme = useCallback((id) => {
    const next = isValid(id) && id !== "custom" ? id : DEFAULT_THEME;
    try { window.localStorage.setItem(USER_KEY, next); } catch { /* ignore */ }
    setUserThemeState(next);
  }, []);

  /**
   * Set theme for a specific restaurant. When `id === "custom"` you must
   * also pass `accent` as a `#RRGGBB` hex string.
   */
  const setRestaurantTheme = useCallback((id, accent = null, rid = restaurantId) => {
    if (!rid) return;
    const next = isValid(id) ? id : DEFAULT_THEME;
    if (next === "custom" && !isHex(accent)) return;

    setOverrides((prev) => {
      const map = { ...prev, [rid]: next };
      writeMap(map);
      return map;
    });
    if (next === "custom") {
      setAccents((prev) => {
        const map = { ...prev, [rid]: accent.toLowerCase() };
        writeAccentMap(map);
        return map;
      });
    } else {
      setAccents((prev) => {
        const map = { ...prev };
        delete map[rid];
        writeAccentMap(map);
        return map;
      });
    }
    api.setRestaurantBrandingTheme(rid, next, accent).catch(() => { /* silent */ });
  }, [restaurantId]);

  /** Remove the override for a specific restaurant (or the current one). */
  const clearRestaurantTheme = useCallback((rid = restaurantId) => {
    if (!rid) return;
    setOverrides((prev) => {
      const map = { ...prev };
      delete map[rid];
      writeMap(map);
      return map;
    });
    setAccents((prev) => {
      const map = { ...prev };
      delete map[rid];
      writeAccentMap(map);
      return map;
    });
    api.setRestaurantBrandingTheme(rid, null).catch(() => { /* silent */ });
  }, [restaurantId]);

  /**
   * Smart setter used by the topbar toggle (presets only — for custom use the
   * settings page where the picker lives).
   */
  const setTheme = useCallback((id) => {
    if (id === "custom") return; // requires hex; not supported via topbar shortcut
    if (validOverride && restaurantId) {
      setRestaurantTheme(id, null, restaurantId);
    } else {
      setUserTheme(id);
    }
  }, [validOverride, restaurantId, setRestaurantTheme, setUserTheme]);

  return {
    theme: activeTheme,
    accent: activeAccent,
    userTheme,
    restaurantTheme: validOverride,
    restaurantAccent: validAccent,
    hasRestaurantOverride: !!validOverride,
    themes: COLOR_THEMES,
    setTheme,
    setUserTheme,
    setRestaurantTheme,
    clearRestaurantTheme,
  };
}

/**
 * Synchronous initialiser that runs as early as possible (in index.js)
 * to avoid the flash of default coral theme on first paint. Reads the
 * restaurant-level override for the currently saved restaurant, falling
 * back to the user default.
 */
export function initColorTheme() {
  try {
    const userTheme = readUser();
    const map = readMap();
    const accentMap = readAccentMap();
    const ridFromStorage = window.localStorage.getItem("currentRestaurantId");
    const override = ridFromStorage && isValid(map[ridFromStorage]) ? map[ridFromStorage] : null;
    const accent = ridFromStorage && override === "custom" && isHex(accentMap[ridFromStorage]) ? accentMap[ridFromStorage] : null;
    applyTheme(override || userTheme, accent);
  } catch { /* ignore */ }
}

/**
 * Reconcile local overrides with the backend `branding.theme` and
 * `branding.accent_color` of each restaurant. Backend wins.
 */
export function syncBackendThemes(restaurants) {
  if (!Array.isArray(restaurants) || restaurants.length === 0) return;
  try {
    const current = readMap();
    const accentCurrent = readAccentMap();
    let changed = false;
    let accentChanged = false;
    for (const r of restaurants) {
      const rid = r?.restaurant_id;
      const beTheme = r?.branding?.theme;
      const beAccent = r?.branding?.accent_color;
      if (!rid) continue;
      if (beTheme && isValid(beTheme)) {
        if (current[rid] !== beTheme) {
          current[rid] = beTheme;
          changed = true;
        }
        if (beTheme === "custom" && isHex(beAccent)) {
          if (accentCurrent[rid] !== beAccent) {
            accentCurrent[rid] = beAccent;
            accentChanged = true;
          }
        } else if (accentCurrent[rid]) {
          delete accentCurrent[rid];
          accentChanged = true;
        }
      }
    }
    if (changed) writeMap(current);
    if (accentChanged) writeAccentMap(accentCurrent);
    if (changed || accentChanged) {
      const ridFromStorage = window.localStorage.getItem("currentRestaurantId");
      const userTheme = readUser();
      const override = ridFromStorage && isValid(current[ridFromStorage]) ? current[ridFromStorage] : null;
      const accent = override === "custom" && isHex(accentCurrent[ridFromStorage]) ? accentCurrent[ridFromStorage] : null;
      applyTheme(override || userTheme, accent);
      window.dispatchEvent(new StorageEvent("storage", {
        key: RESTAURANT_KEY,
        newValue: JSON.stringify(current),
      }));
      window.dispatchEvent(new StorageEvent("storage", {
        key: ACCENT_KEY,
        newValue: JSON.stringify(accentCurrent),
      }));
    }
  } catch { /* ignore */ }
}

export default useColorTheme;
